// src/components/messages/NewMessageForm.js
import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { messagingService } from '../../services/messagingService';
import { listPropertiesForCurrentUser } from '../../services/accApplicationsService';
import { PropertyCombobox } from '../ui';
import AttachmentUploader from './AttachmentUploader';

import { useAuth } from '../../contexts/AuthContext';
import { useHOA } from '../../contexts/HOAContext';

const ACC_PROJECT_TYPES = [
  'Fence', 'Deck / Patio', 'Roof', 'Shed / Outbuilding',
  'Landscaping', 'Driveway', 'Addition / Expansion',
  'Paint / Exterior Finish', 'Garage', 'Other',
];

const NewMessageForm = ({ onClose, onSent }) => {
  const { user, impersonating, effectiveRole } = useAuth();
  const { hoa } = useHOA();
  const [type, setType] = useState('general');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [projectType, setProjectType] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isAcc = type === 'acc';
  const selectedProperty = useMemo(
    () => properties.find((p) => p.id === propertyId) || null,
    [properties, propertyId]
  );
  const canSubmit = subject.trim() && body.trim() && (!isAcc || (projectType && propertyId));
  const fieldClassName = 'w-full px-4 py-3 text-base md:text-sm rounded-2xl bg-white border-2 border-amber-300/80 hover:border-secondary/70 focus:border-secondary focus:outline-none focus:ring-4 focus:ring-secondary/15 text-primary-dark placeholder-slate-400 transition-all';

  useEffect(() => {
    if (!isAcc || !hoa?.id || properties.length > 0) return undefined;
    let mounted = true;
    setPropertiesLoading(true);
    listPropertiesForCurrentUser(hoa.id).then(
      (result) => {
        if (!mounted) return;
        setPropertiesLoading(false);
        if (result.error) {
          console.warn('NewMessageForm properties load failed:', result.error.message);
          setProperties([]);
          return;
        }
        setProperties(result.data || []);
      },
      (loadError) => {
        if (!mounted) return;
        setPropertiesLoading(false);
        console.warn('NewMessageForm properties load failed:', loadError?.message || loadError);
      }
    );
    return () => {
      mounted = false;
    };
  }, [isAcc, hoa?.id, properties.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;

    setSubmitting(true);
    setError('');

    try {
      const address = selectedProperty?.situs_address || '';
      const accDetails = isAcc
        ? { project_type: projectType, address, property_id: propertyId || null }
        : null;
      const thread = await messagingService.createThread({ type, subject, body, accDetails, hoaId: hoa.id });

      if (isAcc && files.length > 0) {
        await messagingService.uploadAttachments(thread.id, files);
      }

      const targetUserId = impersonating ? impersonating.id : user?.id;
      if (targetUserId) {
        await messagingService.markRead(thread.id, targetUserId);
      }

      onSent?.();
      onClose?.();
    } catch (err) {
      setError(err.message || 'Failed to send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      className="bg-white rounded-2xl border-2 border-amber-300/70 warm-tactile-shadow p-6 max-md:fixed max-md:left-0 max-md:right-0 max-md:top-[max(4.75rem,env(safe-area-inset-top))] max-md:bottom-0 max-md:z-[80] max-md:rounded-t-[2rem] max-md:rounded-b-2xl max-md:overflow-y-auto max-md:p-4"
    >
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h3 className="font-display font-bold text-primary-dark text-lg">New Message</h3>
          <p className="text-sm font-medium text-slate-500 mt-1">Send a message or ACC request to the HOA team.</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full text-slate-400 hover:text-primary-dark hover:bg-section-bg flex items-center justify-center transition-all"
            aria-label="Close new message form"
          >
            <span className="material-symbols-outlined text-xl">close</span>
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-bold text-primary-dark mb-2">Message type</label>
          <div className="flex gap-2 flex-wrap rounded-[2rem] bg-section-bg border-2 border-amber-300/70 p-1.5">
            {[
              { value: 'general', label: 'Message to Board & Management' },
              { value: 'acc', label: 'ACC Application' },
            ].map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => { setType(opt.value); setFiles([]); }}
                className={`flex-1 min-w-[12rem] py-2.5 px-4 rounded-full text-sm font-bold border transition-all ${
                  type === opt.value
                    ? 'bg-secondary text-white border-secondary'
                    : 'bg-white text-primary-dark border-amber-300/80 hover:border-secondary/70'
                }`}
              >
                {opt.label}
              </button>
            ))}
            {(effectiveRole === 'board' || effectiveRole === 'admin' || effectiveRole === 'management') && (
              <button
                type="button"
                onClick={() => setType('announcement')}
                className={`px-4 py-2 rounded-full text-sm font-bold border transition-all ${
                  type === 'announcement'
                    ? 'bg-secondary text-white border-secondary'
                    : 'bg-white text-primary-dark border-amber-300/80 hover:border-secondary/70'
                }`}
              >
                Community Announcement
              </button>
            )}
          </div>
        </div>

        <AnimatePresence>
          {isAcc && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-3 overflow-hidden"
            >
              <div className="p-4 rounded-2xl bg-section-bg border-2 border-secondary/25 text-xs font-medium text-primary-dark leading-relaxed">
                ACC applications are reviewed by the Architectural Control Committee.
                Preliminary review does not constitute formal board approval. Board has 30 days to respond per CC&Rs.
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-widest">Project type *</label>
                <select
                  value={projectType}
                  onChange={e => setProjectType(e.target.value)}
                  required={isAcc}
                  className={fieldClassName}
                >
                  <option value="">Select type</option>
                  {ACC_PROJECT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-widest">Property address *</label>
                <PropertyCombobox
                  testId="acc-property-select"
                  properties={properties}
                  value={propertyId}
                  onChange={setPropertyId}
                  disabled={propertiesLoading}
                />
                {propertiesLoading && (
                  <p className="text-xs font-medium text-slate-500 mt-2">Loading properties…</p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div>
          <label className="block text-sm font-bold text-primary-dark mb-1.5">Subject *</label>
          <input
            type="text"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            required
            placeholder={isAcc ? 'e.g. Cedar fence installation - 6ft' : type === 'announcement' ? 'Announcement subject...' : 'e.g. Street light outage on Mountain View'}
            className={fieldClassName}
          />
        </div>

        <div>
          <label className="block text-sm font-bold text-primary-dark mb-1.5">
            {isAcc ? 'Project description *' : 'Message *'}
          </label>
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            required
            rows={5}
            placeholder={isAcc
              ? 'Describe your project in detail: materials, dimensions, colors, location on property...'
              : type === 'announcement'
              ? 'Write your community announcement...'
              : 'Describe your question or concern...'
            }
            className={`${fieldClassName} resize-none min-h-[130px]`}
          />
        </div>

        {isAcc && (
          <div>
            <label className="block text-sm font-bold text-primary-dark mb-1.5">Attachments</label>
            <AttachmentUploader files={files} onChange={setFiles} />
          </div>
        )}

        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3 font-bold">{error}</p>
        )}

        <div className="mobile-sticky-actions flex flex-col-reverse sm:flex-row justify-end gap-2 pt-1 bg-white">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-5 py-3 text-sm text-slate-700 hover:text-primary-dark rounded-full border-2 border-amber-300/80 hover:bg-section-bg transition-all font-bold"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={!canSubmit || submitting}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-3 bg-secondary text-white text-sm font-bold rounded-full hover:brightness-110 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <span className={`material-symbols-outlined text-[18px] ${submitting ? 'animate-spin' : ''}`}>
              {submitting ? 'progress_activity' : 'send'}
            </span>
            {submitting ? 'Sending...' : 'Send'}
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default NewMessageForm;
